// The way between a Poneva item and the outside agents the person works in.
//
// Back out: each of the person's connected agents with a web home (GET
// /api/v1/company/agent-access/return-links, src/mcp-access/everywhere/way-back.mjs)
// is offered as "Open in <its name>", opening that home; there the person asks
// the agent to open the item through its Poneva connection. The agent, its name
// and its address come from the person's own connections: no vendor-specific
// links, and no entry for an agent without a web home.
//
// In: an agent's share link (…&share=1, share_poneva_item) opens the item's
// share sheet once, so sharing stays the person's choice in Poneva.
import { useEffect, useState } from 'react';
import { companyRequest } from '@/company/api';

export type AgentWayBack = { name: string; origin: string; host: string };

const RETURN_LINKS = '/api/v1/company/agent-access/return-links';
// Connections change rarely; a canvas opened within this long reuses the answer.
const FRESH_MS = 60_000;

function parseAgents(body: unknown): AgentWayBack[] {
  const list = (body as { agents?: unknown } | null)?.agents;
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry) => {
    const { name, origin, host } = (entry ?? {}) as Record<string, unknown>;
    if (typeof name !== 'string' || !name || typeof host !== 'string') return [];
    if (typeof origin !== 'string' || !origin.startsWith('https://')) return [];
    return [{ name, origin, host }];
  });
}

let cached: { key: string; at: number; agents: Promise<AgentWayBack[]> } | null = null;

/** The person's agents with a way back, read once a minute per company; none when it fails. */
export function loadAgentWayBack(key: string) {
  if (cached?.key === key && Date.now() - cached.at < FRESH_MS) return cached.agents;
  const agents = companyRequest<unknown>(RETURN_LINKS).then(parseAgents, () => []);
  cached = { key, at: Date.now(), agents };
  return agents;
}

/**
 * The account and company the way back is read for: only while the company
 * has the workspace tools (agentFeatures.everywhere) and both are known.
 */
export const wayBackKey = (
  everywhere: boolean | undefined,
  userId: string | undefined,
  companyId: string | undefined,
) => (everywhere === true && userId && companyId ? `${userId}:${companyId}` : null);

/** The agents an open item offers a way back to: a loaded Result only. */
export const wayBackFor = (kind: string | undefined, loaded: boolean, agents: AgentWayBack[]) =>
  kind === 'result' && loaded ? agents : [];

/** `companyKey` names the account and company the canvas shows; null when the feature is off. */
export function useAgentWayBack(companyKey: string | null) {
  const [agents, setAgents] = useState<AgentWayBack[]>([]);
  useEffect(() => {
    setAgents([]);
    if (!companyKey) return;
    let current = true;
    void loadAgentWayBack(companyKey).then((list) => {
      if (current) setAgents(list);
    });
    return () => {
      current = false;
    };
  }, [companyKey]);
  return agents;
}

export const openAgent = (agent: AgentWayBack) => window.open(agent.origin, '_blank', 'noopener');

let consumedShare: string | null = null;

// The address without the share request, so a reload or a copied link does
// not open the sheet again. replaceState moves no route: no hash event fires.
function dropShareRequest(hash: string) {
  const [path, query = ''] = hash.split('?');
  const params = new URLSearchParams(query);
  params.delete('share');
  const rest = params.toString();
  const { history } = window;
  if (typeof history?.replaceState !== 'function') return;
  history.replaceState(history.state, '', `${path}${rest ? `?${rest}` : ''}`);
}

/** An agent's share link (…&share=1) opens that item's share sheet once per link. */
export function consumeShareRequest(id: string) {
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.split('?')[1] ?? '');
  if (params.get('share') !== '1' || params.get('id') !== id || consumedShare === hash)
    return false;
  consumedShare = hash;
  // After this render: the sheet opens from the request, then the address forgets it.
  queueMicrotask(() => {
    if (window.location.hash === hash) dropShareRequest(hash);
  });
  return true;
}
